import { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Compass, Gamepad2, Download, MessageSquare, Stamp, Rocket, MapPin, Thermometer, UserPlus, MoveHorizontal } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { useSiteConfig } from "../context/SiteConfig";

const FOX = "https://customer-assets-wrfwihn1.emergentagent.net/job_springs-roleplay/artifacts/sydwp9bo_Untitled%20design.gif";
const FOX_W = 68;

const STEPS = [
  { icon: Gamepad2, title: "Own RDR2 or Red Dead Online", desc: "You'll need a legitimate copy of either game." },
  { icon: Download, title: "Download RedM", desc: "Install RedM using the provided link." },
  { icon: MessageSquare, title: "Join Our Discord", desc: "Join the Aurora Springs Discord using the provided link." },
  { icon: Stamp, title: "Get Your Visa", desc: "Obtain the Visa role or complete a Whitelist Application." },
  { icon: Rocket, title: "Launch RedM", desc: "Open Steam first, then launch RedM." },
  { icon: MapPin, title: "Find Aurora Springs", desc: "Search \u201CAurora Springs\u201D in the RedM server browser and connect." },
  { icon: Thermometer, title: "Adjust Your Settings", desc: "Before creating your character, change your temperature setting to Celsius in game." },
  { icon: UserPlus, title: "Create Your Character", desc: "Customize your character and begin your story!" },
];

export const JoinTrail = () => {
  const { link } = useSiteConfig();
  const trackRef = useRef(null);
  const [w, setW] = useState(0);
  const [active, setActive] = useState(0);
  const x = useMotionValue(0);
  const n = STEPS.length;
  const maxX = Math.max(0, w - FOX_W);
  const nodeX = (i) => (n <= 1 ? 0 : (i / (n - 1)) * maxX);

  useEffect(() => {
    const measure = () => setW(trackRef.current ? trackRef.current.offsetWidth : 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const onDrag = () => {
    if (maxX <= 0) return;
    const r = Math.min(1, Math.max(0, x.get() / maxX));
    setActive(Math.round(r * (n - 1)));
  };
  const goTo = (i) => { setActive(i); animate(x, nodeX(i), { type: "spring", stiffness: 300, damping: 30 }); };

  const Step = STEPS[active];

  return (
    <div>
      <Reveal><div className="flex items-center gap-3"><Compass className="h-5 w-5 text-teal" /><Overline>How to Join</Overline></div></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-light">Your trail into <span className="text-teal">Aurora Springs</span></h2>
        <p className="mt-3 max-w-xl text-[#8F877C] leading-relaxed">Drag the fox along the trail to follow every step from install to your first day in town.</p>
      </Reveal>

      {/* Interactive trail — desktop */}
      <div className="mt-14 hidden lg:block">
        <div ref={trackRef} className="relative" style={{ height: 150 }} data-testid="join-trail-track">
          <div className="absolute left-0 right-0 top-[25px] h-[3px] bg-[#2A201A]" />
          <div className="absolute left-0 top-[25px] h-[3px] bg-teal transition-[width] duration-300 ease-out" style={{ width: `${(active / (n - 1)) * 100}%` }} />

          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i <= active;
            return (
              <button key={i} type="button" onClick={() => goTo(i)} data-testid={`join-step-${i}`}
                className="absolute flex -translate-x-1/2 flex-col items-center text-center focus:outline-none group"
                style={{ left: `${(i / (n - 1)) * 100}%`, width: 132, top: 0 }}>
                <span className={`flex h-[52px] w-[52px] items-center justify-center rounded-full border-2 transition-colors duration-300 ${done ? "bg-teal border-teal text-obsidian" : "bg-obsidian border-[#3a2f27] text-[#6b6157] group-hover:border-teal/60"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8F877C]">Step {i + 1}</span>
                <span className={`mt-1.5 font-display text-sm leading-snug transition-colors ${done ? "text-bone" : "text-[#6b6157]"}`}>{s.title}</span>
              </button>
            );
          })}

          <motion.div drag="x" dragConstraints={{ left: 0, right: maxX }} dragElastic={0.02}
            style={{ x, top: -6, width: FOX_W }} onDrag={onDrag} onDragEnd={onDrag}
            data-testid="join-trail-fox"
            className="absolute z-10 cursor-grab active:cursor-grabbing">
            <img src={FOX} alt="Drag me along the trail" draggable={false}
              style={{ filter: "brightness(0) invert(0.85) drop-shadow(0 2px 4px rgba(0,0,0,0.85))" }}
              className="w-full select-none pointer-events-none" />
          </motion.div>
        </div>

        <div className="mt-2 flex items-center gap-2 font-mono text-xs text-[#5c554c]">
          <MoveHorizontal className="h-3.5 w-3.5" /> Click and drag the fox — or tap a step
        </div>

        <Reveal>
          <div className="mt-8 border-l-2 border-teal bg-surface/40 p-6 max-w-2xl" data-testid="join-step-detail">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal">Step {active + 1} of {n}</p>
            <p className="mt-2 font-display text-2xl font-light text-bone">{Step.title}</p>
            <p className="mt-2 text-[#b8b0a4] leading-relaxed">{Step.desc}</p>
            {active === 1 && (
              <a href="https://redm.net/" target="_blank" rel="noreferrer" data-testid="join-trail-redm"
                className="mt-4 inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-5 py-2.5 hover:bg-bone transition-colors">
                <Download className="h-4 w-4" /> Download RedM
              </a>
            )}
            {active === 2 && (
              <a href={link("discord")} target="_blank" rel="noreferrer" data-testid="join-trail-discord"
                className="mt-4 inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-5 py-2.5 hover:bg-bone transition-colors">
                <MessageSquare className="h-4 w-4" /> Join Discord
              </a>
            )}
          </div>
        </Reveal>
      </div>

      {/* Vertical stepper — mobile / tablet */}
      <div className="mt-10 grid gap-px bg-[#2A201A] lg:hidden">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="flex gap-4 bg-obsidian p-5" data-testid={`join-step-mobile-${i}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-teal/40 bg-teal/10 text-teal">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8F877C]">Step {i + 1}</p>
                <p className="font-display text-lg font-light text-bone">{s.title}</p>
                <p className="mt-0.5 text-sm text-[#8F877C] leading-relaxed">{s.desc}</p>
                {i === 1 && (
                  <a href="https://redm.net/" target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2 hover:bg-bone transition-colors">
                    <Download className="h-4 w-4" /> Download RedM
                  </a>
                )}
                {i === 2 && (
                  <a href={link("discord")} target="_blank" rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2 hover:bg-bone transition-colors">
                    <MessageSquare className="h-4 w-4" /> Join Discord
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
